import { EMPTY, merge, Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  scan,
  shareReplay,
  startWith,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

export type ResourceSetValue$<ResourceState, Params> = (
  resourceSnapshot: () => ResourceLike<ResourceState, Params> | undefined,
) => Observable<ResourceState | undefined>;

export type ResourceReload$<ResourceState, Params> = (
  resourceSnapshot: () => ResourceLike<ResourceState, Params> | undefined,
) => Observable<unknown>;
/**
 *
 * @param param$ Passing undefined will return the idle resource and cancel the loader
 * @param loader The loader function to load the resource
 * @param initialValue The initial value of the resource
 * @param initialParams The initial params of the resource
 * @param setValue$ The set value function to set the value of the resource
 * @param reload$ The reload function to reload the resource
 * @returns The resource stream
 */
export function resource$<ResourceState, Params>({
  params$,
  loader,
  initialValue,
  initialParams,
  setValue$,
  destroy$,
  reload$,
}: {
  params$: Observable<Params>;
  loader: (param: { params: Params }) => Observable<ResourceState>;
  initialValue?: NoInfer<ResourceState>;
  initialParams?: NoInfer<Params>;
  setValue$?: ResourceSetValue$<NoInfer<ResourceState>, NoInfer<Params>>;
  reload$?: ResourceReload$<ResourceState, Params>;
  destroy$: Observable<unknown>;
}) {
  let resourceSnapshot: undefined | ResourceLike<ResourceState, Params> = undefined;
  let parmasSnapshot: undefined | Params = initialParams;
  const dataFromLoader$ = merge(
    params$,
    reload$?.(() => resourceSnapshot)?.pipe(
      filter(params => !!params),
      map(() => parmasSnapshot as Params),
    ) ?? EMPTY,
  ).pipe(
    tap(params => (parmasSnapshot = params)),
    switchMap(params =>
      typeof params === 'undefined'
        ? of(idleResource(initialValue, initialParams) as ResourceLike<ResourceState, Params>)
        : loader({ params }).pipe(
            map(
              value =>
                ({
                  hasValue: true,
                  isLoading: false,
                  status: 'resolved',
                  hasError: false,
                  error: undefined,
                  value,
                  params,
                }) as ResourceLike<ResourceState, Params>,
            ),
            startWith({
              hasValue: !!initialValue,
              isLoading: true,
              status: 'loading',
              hasError: false,
              error: undefined,
              value: initialValue,
              params,
            } as ResourceLike<ResourceState, Params>),
            catchError(error =>
              of({
                hasValue: false,
                isLoading: false,
                status: 'error',
                hasError: true,
                error,
                value: undefined,
                params,
              } as ResourceLike<ResourceState, Params>),
            ),
          ),
    ),
  );

  const localSet$ =
    setValue$?.(() => resourceSnapshot as ResourceLike<ResourceState, Params>).pipe(
      map(
        value =>
          ({
            hasValue: !!value,
            isLoading: false,
            status: 'local',
            hasError: true,
            error: undefined,
            value: value,
            params: undefined,
          }) as ResourceLike<ResourceState, Params>,
      ),
    ) ?? EMPTY;

  const stream$ = merge(dataFromLoader$, localSet$).pipe(
    // used to preserve the last value
    scan(
      (acc, cur) => {
        if (!cur.hasValue && !cur.hasError) {
          return {
            ...cur,
            value: acc.value,
          };
        }
        return cur;
      },
      idleResource<ResourceState, Params>(initialValue, initialParams),
    ),
    startWith(idleResource(initialValue, initialParams) as ResourceLike<ResourceState, Params>),
    tap(resource => (resourceSnapshot = resource)),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );
  stream$.pipe(takeUntil(destroy$)).subscribe();
  return Object.assign(stream$, {
    snapshot: () => resourceSnapshot,
    params: () => parmasSnapshot,
  });
}

export type ResourceLike<State, Params> = {
  readonly isLoading: boolean;
  readonly hasValue: boolean;
  readonly status: ResourceStatus;
  readonly hasError: boolean;
  readonly error: any | undefined;
  readonly value: State | undefined;
  readonly params: Params | undefined;
};

export const idleResource = <ResourceState, Params>(
  initialValue?: ResourceState,
  initialParams?: Params,
): ResourceLike<ResourceState, Params> => ({
  hasValue: !!initialValue,
  isLoading: false,
  status: 'idle',
  hasError: false,
  error: undefined,
  value: initialValue,
  params: initialParams,
});
type ResourceStatus = 'idle' | 'error' | 'loading' | 'reloading' | 'resolved' | 'local';
